import { useState, useCallback, useRef } from 'react';
import { leadService } from '../../verve-leads/leads/services/leadService';

export const useCustomerDetails = () => {
  const [leadCache, setLeadCache] = useState({});
  const [currentLead, setCurrentLead] = useState(null);
  const [loadingLead, setLoadingLead] = useState(false);
  const [isCustomerDrawerOpen, setIsCustomerDrawerOpen] = useState(false);
  const activePhoneRef = useRef(null);

  const fetchCustomerDetails = useCallback(async (phone) => {
    if (!phone) return;
    
    // Normalize phone number for search (remove +, -, spaces)
    const normalizedPhone = phone.replace(/[\s\+\-]/g, '');
    
    // Caching logic
    if (leadCache[normalizedPhone]) {
      setCurrentLead(leadCache[normalizedPhone]);
      return;
    }

    setLoadingLead(true);
    setCurrentLead(null);
    activePhoneRef.current = normalizedPhone;

    try {
      // Searching by phone
      const res = await leadService.getLeads({ search: normalizedPhone });
      // Depending on API response structure, we might get an array or object
      const data = res?.data?.data || res?.data || [];
      const foundLead = Array.isArray(data) && data.length > 0 ? data[0] : null;

      if (activePhoneRef.current === normalizedPhone) {
        if (foundLead) {
          setCurrentLead(foundLead);
          setLeadCache(prev => ({ ...prev, [normalizedPhone]: foundLead }));
        } else {
          // If no lead found, store a dummy 'not_found' flag to avoid refetching
          setCurrentLead({ not_found: true });
          setLeadCache(prev => ({ ...prev, [normalizedPhone]: { not_found: true } }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch lead details:', error);
      if (activePhoneRef.current === normalizedPhone) {
        setCurrentLead({ not_found: true });
      }
    } finally {
      if (activePhoneRef.current === normalizedPhone) {
        setLoadingLead(false);
      }
    }
  }, [leadCache]);

  const toggleCustomerDrawer = useCallback(() => {
    setIsCustomerDrawerOpen(prev => !prev);
  }, []);

  const closeCustomerDrawer = useCallback(() => {
    setIsCustomerDrawerOpen(false);
  }, []);

  const openCustomerDrawer = useCallback((phone) => {
    setIsCustomerDrawerOpen(true);
    fetchCustomerDetails(phone);
  }, [fetchCustomerDetails]);

  return {
    isCustomerDrawerOpen,
    toggleCustomerDrawer,
    closeCustomerDrawer,
    openCustomerDrawer,
    currentLead,
    loadingLead
  };
};
